import { apiClient, apiClientBlob } from '@/lib/api-client';
import { forgetClosedRegisterSession } from '@/stores/use-register-store';

/**
 * Ops simulation T08 — the customer's linked credit account, read by the
 * payment screen to offer "Account Credit". Amounts in minor units.
 */
export interface CustomerCreditAccount {
  id: string;
  customer_id: string;
  customer_name: string;
  /** 0 means the account has no limit. */
  credit_limit: number;
  outstanding_balance: number;
  /** credit_limit − outstanding; null when there is no limit. */
  available: number | null;
}

/** An open credit invoice, for "One invoice" in Receive payment (L03). Minor units. */
export interface OpenCreditInvoice {
  id: string;
  invoice_number: string;
  amount: number;
  paid_amount: number;
  balance: number;
  status: 'OUTSTANDING' | 'PARTIALLY_PAID';
  created_at: string;
}

export type OnAccountMethod = 'CASH' | 'UPI' | 'CARD';

export interface ReceiveOnAccountDto {
  amount: number;
  payment_method: OnAccountMethod;
  reference_number?: string;
  location_id: string;
  register_session_id?: string;
  credit_invoice_id?: string;
}

/** What POST /credit-accounts/:id/payments answers (L03). Minor units. */
export interface OnAccountPaymentReceipt {
  receipt: {
    id: string;
    receipt_number: string;
    amount: number;
    payment_method: OnAccountMethod;
    reference_number: string | null;
    currency: string;
    location_id: string;
    register_session_id: string | null;
    customer_name: string | null;
    received_by_name: string | null;
    cashier_name: string | null;
    created_at: string;
  };
  allocations: Array<{
    credit_invoice_id: string;
    invoice_number: string;
    amount: number;
    balance_after: number;
    status: string;
  }>;
  account: {
    id: string;
    outstanding_balance: number;
    credit_limit: number;
    available: number | null;
  };
}

export const creditAccountService = {
  /**
   * The customer's active credit account, or null when they have none. The
   * endpoint is gated by the credit-billing flag and plan, so a 403 also means
   * "not offered" — callers treat any failure the same way.
   */
  getForCustomer(customerId: string) {
    return apiClient<CustomerCreditAccount | null>(
      `/credit-accounts/customer/${encodeURIComponent(customerId)}`,
    );
  },

  /**
   * L03: the account's open invoices, oldest first (the order a payment settles
   * them). The list's status filter takes one value, so PAID / WRITTEN_OFF are
   * dropped here.
   */
  async listOpenInvoices(accountId: string): Promise<OpenCreditInvoice[]> {
    const res = await apiClient<{ data: Array<OpenCreditInvoice & { status: string }> }>(
      `/credit-invoices?account_id=${encodeURIComponent(accountId)}&limit=100`,
    );
    return (res.data ?? [])
      .filter((i) => (i.status === 'OUTSTANDING' || i.status === 'PARTIALLY_PAID') && i.balance > 0)
      .sort((a, b) => (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0)) as OpenCreditInvoice[];
  },

  /**
   * L03: take a payment on the account at the till. `idempotencyKey` is one id
   * per Receive payment window, reused on a retry, so a resend is replayed and
   * the money is never taken twice.
   */
  async receivePayment(accountId: string, dto: ReceiveOnAccountDto, idempotencyKey: string) {
    try {
      return await apiClient<OnAccountPaymentReceipt>(
        `/credit-accounts/${encodeURIComponent(accountId)}/payments`,
        { method: 'POST', body: dto, headers: { 'X-Idempotency-Key': idempotencyKey } },
      );
    } catch (err) {
      if ((err as { code?: string } | null)?.code === 'REGISTER_SESSION_NOT_OPEN') forgetClosedRegisterSession();
      throw err;
    }
  },

  /** L03: a payment receipt again, for a reprint. */
  getPaymentReceipt(receiptId: string) {
    return apiClient<OnAccountPaymentReceipt>(`/credit-accounts/payments/${encodeURIComponent(receiptId)}`);
  },

  /** L03: the payment slip as a PDF, for a till with no thermal printer. */
  getPaymentReceiptPdf(receiptId: string, format: '80mm' | '58mm' = '80mm') {
    return apiClientBlob(
      `/credit-accounts/payments/${encodeURIComponent(receiptId)}/receipt?format=${format}`,
    );
  },
};
